import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  ResultDialog,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { jobsApi } from "@/services/api";
import {
  Clock,
  Play,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Calendar,
  Timer,
  AlertCircle,
  Pause,
} from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/utils";

interface JobDefinition {
  id: string;
  name: string;
  displayName: string;
  description: string;
  cronExpression: string;
  timezone: string;
  isEnabled: boolean;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastRunMessage: string | null;
  nextRunAt: string | null;
  totalRuns: number;
  successfulRuns: number;
  failedRuns: number;
  isRegistered: boolean;
}

interface JobExecution {
  id: string;
  jobName: string;
  jobDescription: string;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  success: boolean | null;
  message: string | null;
  details: Record<string, unknown> | null;
  errorMessage: string | null;
  triggeredBy: string;
}

interface JobStats {
  totalJobs: number;
  enabledJobs: number;
  last24h: {
    total: number;
    success: number;
    failed: number;
    running: number;
  };
}

export function JobsPage() {
  const [jobs, setJobs] = useState<JobDefinition[]>([]);
  const [executions, setExecutions] = useState<JobExecution[]>([]);
  const [stats, setStats] = useState<JobStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [runningJob, setRunningJob] = useState<string | null>(null);
  const [resultDialog, setResultDialog] = useState<{
    open: boolean;
    title: string;
    description?: string;
    details?: { label: string; value: string | number }[];
    variant: "success" | "error" | "info";
  }>({
    open: false,
    title: "",
    variant: "info",
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [jobsData, statsData, executionsData] = await Promise.all([
        jobsApi.list(),
        jobsApi.getStats(),
        jobsApi.getExecutions(undefined, 20),
      ]);
      setJobs(jobsData);
      setStats(statsData);
      setExecutions(executionsData);
    } catch (error) {
      console.error("Erro ao carregar jobs:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  }

  async function handleToggleJob(name: string, currentEnabled: boolean) {
    try {
      await jobsApi.toggleJob(name, !currentEnabled);
      await loadData();
    } catch (error) {
      console.error("Erro ao alternar job:", error);
    }
  }

  async function loadJobExecutions(jobName: string) {
    setSelectedJob(jobName);
    try {
      const data = await jobsApi.getJobExecutions(jobName, 10);
      setExecutions(data);
    } catch (error) {
      console.error("Erro ao carregar execuções:", error);
    }
  }

  async function handleRunJob(name: string) {
    setRunningJob(name);
    try {
      const result = await jobsApi.runJob(name);
      // Recarrega dados para mostrar nova execução
      await loadData();

      if (result.success) {
        // Prepara detalhes se disponíveis
        const details: { label: string; value: string | number }[] = [];
        if (result.details) {
          if (result.details.balanceFormatted) {
            details.push({ label: "Saldo", value: result.details.balanceFormatted });
          }
          if (result.details.emailsSent !== undefined) {
            details.push({ label: "Emails enviados", value: result.details.emailsSent });
          }
          if (result.details.accountInfo) {
            details.push({ label: "Conta", value: result.details.accountInfo });
          }
        }

        setResultDialog({
          open: true,
          title: "Job executado com sucesso",
          description: result.message,
          details: details.length > 0 ? details : undefined,
          variant: "success",
        });
      } else {
        setResultDialog({
          open: true,
          title: "Erro ao executar job",
          description: result.message,
          variant: "error",
        });
      }
    } catch (error: any) {
      console.error("Erro ao executar job:", error);
      setResultDialog({
        open: true,
        title: "Erro ao executar job",
        description: error.message || "Erro desconhecido",
        variant: "error",
      });
    } finally {
      setRunningJob(null);
    }
  }

  function getStatusBadge(status: string | null, success: boolean | null) {
    if (status === "running") {
      return (
        <Badge variant="warning" className="gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          Executando
        </Badge>
      );
    }
    if (success === true) {
      return (
        <Badge variant="success" className="gap-1">
          <CheckCircle className="w-3 h-3" />
          Sucesso
        </Badge>
      );
    }
    if (success === false) {
      return (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="w-3 h-3" />
          Falha
        </Badge>
      );
    }
    return <Badge variant="secondary">-</Badge>;
  }

  function formatDuration(ms: number | null): string {
    if (!ms) return "-";
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  }

  function formatRelativeTime(dateStr: string | null): string {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Agora";
    if (diffMins < 60) return `${diffMins}min atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    return `${diffDays}d atrás`;
  }

  function formatNextRun(dateStr: string | null): string {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 0) return "Pendente";
    if (diffMins < 60) return `em ${diffMins}min`;
    if (diffHours < 24) return `em ${diffHours}h`;
    return formatDate(dateStr);
  }

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<Clock className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Jobs Agendados"
          description="Monitore os jobs automáticos do sistema"
        />
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Clock className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Jobs Agendados"
        description="Monitore os jobs automáticos do sistema"
        stats={[
          { value: stats?.totalJobs || 0, label: "Total" },
          { value: stats?.last24h.success || 0, label: "Sucesso (24h)" },
          { value: stats?.last24h.failed || 0, label: "Falhas (24h)" },
        ]}
        actions={
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Atualizar
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.totalJobs || 0}</p>
                <p className="text-sm text-muted-foreground">Jobs Registrados</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.last24h.success || 0}</p>
                <p className="text-sm text-muted-foreground">Sucesso (24h)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.last24h.failed || 0}</p>
                <p className="text-sm text-muted-foreground">Falhas (24h)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/20 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.last24h.running || 0}</p>
                <p className="text-sm text-muted-foreground">Em Execução</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Jobs List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Jobs Configurados</CardTitle>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum job registrado ainda.</p>
              <p className="text-sm mt-1">Os jobs serão registrados automaticamente quando o sistema iniciar.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className={`p-4 border rounded-lg hover:bg-muted/50 transition-colors ${
                    selectedJob === job.name ? "border-primary bg-muted/30" : ""
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{job.displayName}</h3>
                        {getStatusBadge(null, job.lastRunStatus === "success")}
                        {!job.isEnabled && (
                          <Badge variant="secondary" className="gap-1">
                            <Pause className="w-3 h-3" />
                            Desativado
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{job.description}</p>

                      <div className="flex flex-wrap gap-4 mt-3 text-sm">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span>Cron: {job.cronExpression}</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Timer className="w-4 h-4" />
                          <span>Última: {formatRelativeTime(job.lastRunAt)}</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Play className="w-4 h-4" />
                          <span>Próxima: {formatNextRun(job.nextRunAt)}</span>
                        </div>
                      </div>

                      {job.lastRunMessage && (
                        <p className="text-sm mt-2 text-muted-foreground">
                          Última mensagem: {job.lastRunMessage}
                        </p>
                      )}

                      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                        <span>Total: {job.totalRuns}</span>
                        <span className="text-green-600">Sucesso: {job.successfulRuns}</span>
                        <span className="text-red-600">Falhas: {job.failedRuns}</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadJobExecutions(job.name)}
                      >
                        Ver Histórico
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleRunJob(job.name)}
                        disabled={runningJob === job.name || !job.isEnabled}
                      >
                        {runningJob === job.name ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            Executando...
                          </>
                        ) : (
                          <>
                            <Play className="w-3 h-3 mr-1" />
                            Executar
                          </>
                        )}
                      </Button>
                      <Button
                        variant={job.isEnabled ? "secondary" : "default"}
                        size="sm"
                        onClick={() => handleToggleJob(job.name, job.isEnabled)}
                      >
                        {job.isEnabled ? "Desativar" : "Ativar"}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Executions History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              Histórico de Execuções
              {selectedJob && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  - {jobs.find((j) => j.name === selectedJob)?.displayName}
                </span>
              )}
            </CardTitle>
            {selectedJob && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedJob(null);
                  loadData();
                }}
              >
                Ver Todos
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {executions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma execução registrada ainda.
            </p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-4 font-medium text-sm">Job</th>
                    <th className="text-left p-4 font-medium text-sm">Status</th>
                    <th className="text-left p-4 font-medium text-sm">Início</th>
                    <th className="text-left p-4 font-medium text-sm">Duração</th>
                    <th className="text-left p-4 font-medium text-sm">Trigger</th>
                    <th className="text-left p-4 font-medium text-sm">Mensagem</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {executions.map((exec) => (
                    <tr key={exec.id} className="hover:bg-muted/50">
                      <td className="p-4 text-sm font-medium">
                        {exec.jobDescription || exec.jobName}
                      </td>
                      <td className="p-4">
                        {getStatusBadge(exec.status, exec.success)}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {formatDateTime(exec.startedAt)}
                      </td>
                      <td className="p-4 text-sm">
                        {formatDuration(exec.durationMs)}
                      </td>
                      <td className="p-4 text-sm">
                        <Badge variant="outline" className="capitalize">
                          {exec.triggeredBy}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground max-w-xs truncate">
                        {exec.errorMessage || exec.message || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de resultado da execução do job */}
      <ResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog({ ...resultDialog, open: false })}
        title={resultDialog.title}
        description={resultDialog.description}
        details={resultDialog.details}
        variant={resultDialog.variant}
        buttonText="Fechar"
      />
    </div>
  );
}
